/**
 * Framework-freie MusicXML-DOM-Helfer (reines DOMParser/XMLSerializer, kein Vue, kein OSMD).
 *
 * Einzige Quelle für die Part-/Takt-/Noten-Trefferlogik und die Divisions-/Dauer-Mathematik –
 * genutzt von den Geschwister-Modulen (`notes`, `measureAttributes`, `lyrics`, …). Die Funktionen
 * sind pur (String rein → String raus) und damit direkt unit-testbar (siehe
 * `src/__tests__/musicXmlDom.spec.ts`).
 *
 * WICHTIG (Part-Scoping): In mehrteiligen Partituren hat JEDER `<part>` eine eigene
 * `<measure number="N">`-Folge. Jede Takt-Suche muss daher zuerst den Part auflösen
 * (`findPart`/`findMeasureInPart`) – ein globales `querySelectorAll('measure')` träfe immer
 * (nur) den ersten Part.
 */
// Aufgeteilt aus dem früheren 2600-Zeilen-Monolithen `musicXmlDom.ts` (Import-Pfad unverändert:
// `@/composables/musicXmlDom` zeigt auf `index.ts`). Abhängigkeitsregel im Ordner: geteilte
// Basis-Helfer liegen in `core.ts`; gerichtete Geschwister-Imports sind erlaubt, ZYKLEN nicht
// (aktuell: bass→chordInference, notes→core, diatonic→notes, structure→notes,
// lyrics→bass, clipboard→structure, fragment→structure).

/**
 * Note information for editing - includes pitch data and editing metadata
 */
export interface NoteInfo {
    measureNumber: number;
    /** Kanonischer 0-basierter Ordinalindex des Takts innerhalb seines Parts. Neue OSMD-basierte
     *  NoteInfos setzen ihn immer; optional bleibt er nur für bestehende API-Clients/Test-Fixtures. */
    measureIndex?: number;
    semitones: number;
    frequency: number;
    accidentalXml?: string;
    accidental?: number;
    noteIndex: number;
    voiceNoteIndex: number; // Position der Note INNERHALB ihrer eigenen Stimme (für XML-Writeback)
    staffIndex: number; // globaler Staff-Index (Staff.idInMusicSheet) – disambiguiert die Notenzeile
                        // bei mehrstimmigen Partituren (mehrere GraphicalMeasure je MeasureNumber)
    partId: string;     // Instrument.IdString == <part id="…"> – für den XML-Writeback (mehrteilig)
    step: string;       // C, D, E, F, G, A, B (empty for rests)
    alter: number;      // -1 = flat, 0 = natural, 1 = sharp
    octave: number;     // 0-9
    duration: string;
    voice: number;
    isRest: boolean;    // true if this is a rest, false if it's a note
}

/**
 * Ein Label, das **nach dem Rendern** im SVG nachgefärbt werden muss.
 *
 * Hintergrund: OSMD liest das `color`-Attribut an genau vier Stellen – `<stem>`, `<notehead>`,
 * `<note>` und Expression-`<words>`. **`<harmony>` und `<lyric>` sind nicht dabei.** Akkordsymbole
 * und Gesangstext bekommen deshalb nie eine XML-Farbe und fallen auf `DefaultColorLabel` bzw.
 * `DefaultColorLyrics` zurück. Wer sie farblich unterscheiden will, muss die gerenderten
 * `<text>`-Knoten nachträglich einfärben – siehe `composables/osmd/useScoreLabelColors.ts`.
 *
 * Der Schlüssel ist bewusst `staffIndex` (nicht `partId`): derselbe Schlüssel wie bei Auswahl,
 * Highlight und DOM-Writeback (siehe `findPartForNote`).
 */
export interface LabelColorTarget {
    staffIndex: number;
    /** 0-basierter Ordinalindex des Takts innerhalb seines Parts. */
    measureIndex: number;
    color: string;
}

/** Ein Label, das an **einer bestimmten Note** hängt (Gesangstext/Bass-Silbe). */
export interface LyricColorTarget extends LabelColorTarget {
    voice: number;
    /** Position der Note innerhalb ihrer eigenen Stimme – dieselbe Zählung wie `getVoiceNotes`. */
    voiceNoteIndex: number;
}


// ============ Parse / Serialize ============

export function parseMusicXml(musicXml: string): Document {
    return new DOMParser().parseFromString(musicXml, 'application/xml');
}

export function serializeMusicXml(doc: Document): string {
    return new XMLSerializer().serializeToString(doc);
}

// ============ Part- / Takt- / Noten-Auflösung ============

/** Löst den Ziel-`<part>` auf: per `id`-Attribut, Fallback = erster Part. */
export function findPart(doc: Document, partId?: string): Element | null {
    const parts = [...doc.querySelectorAll('part')];
    if (!parts.length) return null;
    return parts.find(p => p.getAttribute('id') === partId) ?? parts[0] ?? null;
}

/**
 * Löst den Ziel-`<part>` **einer Note** auf – **ausschließlich über `staffIndex`** (Staffs je Part über
 * `<staves>` akkumuliert, Default 1; letzter Notnagel erster Part). `partId` wird **bewusst NICHT** genutzt:
 * OSMDs `ParentInstrument.IdString` ist bei mehrteiligen Stücken unzuverlässig – mal leer, mal **gleich/
 * falsch für mehrere Parts** (dann matchte ein früherer `partId`-first-Zweig den falschen Part). `staffIndex`
 * (OSMD `idInMusicSheet`) ist dagegen zuverlässig und **derselbe Schlüssel wie Auswahl/Highlight**.
 *
 * **Kritisch (nicht rückbauen):** Der DOM-Writeback jeder per-Note-Bearbeitung
 * (`findPartAndMeasureNote`/`getNoteFromMusicXml`) muss denselben (staffIndex-)Schlüssel wie die Auswahl
 * nutzen – sonst landet bei mehrteiligen Partituren jede Bearbeitung des falschen Parts (Kopieren/Einfügen/
 * Ersetzen/Löschen/Transponieren/Teilen/Update) im **falschen** Part. Die Akkumulation deckt auch
 * Multi-Staff-Parts (`<staves>2</staves>`) korrekt ab.
 */
export function findPartForNote(doc: Document, staffIndex: number): Element | null {
    const parts = [...doc.querySelectorAll('part')];
    if (!parts.length) return null;
    let acc = 0;
    for (const part of parts) {
        const staves = parseInt(part.querySelector('staves')?.textContent || '1', 10);
        if (staffIndex < acc + staves) return part;
        acc += staves;
    }
    return parts[0] ?? null;
}

/** Alle `<part>`-Elemente des Dokuments (für score-weite Operationen wie Tonartwechsel). */
export function getParts(doc: Document): Element[] {
    return [...doc.querySelectorAll('part')];
}

/** Findet `<measure number="N">` INNERHALB eines Parts (nie global suchen – Part-Scoping!). */
export function findMeasureInPart(part: Element, measureNumber: number): Element | null {
    for (const measure of part.querySelectorAll('measure')) {
        const num = parseInt(measure.getAttribute('number') || '0', 10);
        if (num === measureNumber) return measure;
    }
    return null;
}

/** Findet einen Takt über seine 0-basierte Position INNERHALB eines Parts. Anders als das
 *  MusicXML-Attribut `number` ist dieser Index auch bei Pickups, Wiederholungen und mehrfach
 *  vorkommenden/auf `0` gesetzten Taktnummern eindeutig. */
export function findMeasureInPartByIndex(part: Element, measureIndex: number): Element | null {
    if (!Number.isInteger(measureIndex) || measureIndex < 0) return null;
    return part.querySelectorAll('measure').item(measureIndex);
}

/** Neue NoteInfos werden ordinal aufgelöst. Nur alte Nutzlasten ohne `measureIndex` fallen auf die
 *  gedruckte/XML-Taktnummer zurück. Ein vorhandener, aber ungültiger Index darf nicht still einen
 *  gleich nummerierten falschen Takt treffen. */
function findMeasureForNote(part: Element, noteInfo: NoteInfo): Element | null {
    return noteInfo.measureIndex === undefined
        ? findMeasureInPart(part, noteInfo.measureNumber)
        : findMeasureInPartByIndex(part, noteInfo.measureIndex);
}

export function voiceOf(noteEl: Element): number {
    return parseInt(noteEl.querySelector('voice')?.textContent || '1', 10);
}

/** Noten eines Takts, gefiltert auf eine Stimme; innerhalb einer Stimme entspricht die
 *  Dokumentreihenfolge der Zeitreihenfolge (robust gegen `<backup>`/`<forward>`). */
export function getVoiceNotes(measure: Element, voice: number): Element[] {
    return [...measure.querySelectorAll('note')].filter(n => voiceOf(n) === voice);
}

export function noteDurationTicks(noteEl: Element): number {
    return parseInt(noteEl.querySelector('duration')?.textContent || '0', 10);
}

/** Erste `<divisions>` des Parts (music21/OMR-Scores halten sie i. d. R. part-weit konstant). */
export function getPartDivisions(part: Element): number {
    const d = parseInt(part.querySelector('divisions')?.textContent || '0', 10);
    return d > 0 ? d : 0;
}

/** Findet die Zielnote über die Standard-Trefferlogik (part → measure → voice → voiceNoteIndex). */
export function findPartAndMeasureNote(
    doc: Document, noteInfo: NoteInfo,
): { part: Element; measure: Element; note: Element; voiceNotes: Element[] } | null {
    const part = findPartForNote(doc, noteInfo.staffIndex);
    if (!part) return null;
    const measure = findMeasureForNote(part, noteInfo);
    if (!measure) return null;
    const voiceNotes = getVoiceNotes(measure, noteInfo.voice);
    const note = voiceNotes[noteInfo.voiceNoteIndex];
    if (!note) return null;
    return {part, measure, note, voiceNotes};
}

/** Anzeige-Schreibweise einer Tonhöhe aus `NoteInfo`-Feldern: `alter` → ♭/♯ (auch mehrfach),
 *  Oktave angehängt. Beispiele: (G, 0, 5) → "G5", (B, -1, 3) → "B♭3", (F, 1, 4) → "F♯4".
 *  Leerer `step` (Pause) → "". Genutzt von der Info-Zeile des `NoteActionsOverlay`. */
export function formatPitchDisplay(step: string, alter: number, octave: number): string {
    if (!step) return '';
    const steps = Math.trunc(alter) || 0;
    const marks = steps === 0 ? '' : (steps > 0 ? '♯' : '♭').repeat(Math.abs(steps));
    return `${step}${marks}${octave}`;
}

/** music21-Schreibweise einer Tonhöhe: `#` für Kreuze, `-` für Bes, Oktave angehängt.
 *  Beispiele: (B, -1, 3) → "B-3", (F, 1, 4) → "F#4", (C, 0, 4) → "C4", (B, 2, 4) → "B##4".
 *  Leerer `step` (Pause) → "".
 *
 *  Gegenstück zu {@link formatPitchDisplay} (das ♭/♯ für die **Anzeige** setzt): dies ist die
 *  Maschinen-Schreibweise, in der das Backend `AnalysisResult.unmappable_notes` liefert
 *  (music21 `Pitch.nameWithOctave`). Der Abgleich funktioniert nur, weil die Analyse die
 *  **notierten** Tonhöhen derselben MusicXML zählt, die auch der Renderer zeichnet – siehe
 *  `griffschrift/transpose/helper.py::analyze_unmappable_notes`. */
export function music21NameWithOctave(step: string, alter: number, octave: number): string {
    if (!step) return '';
    const steps = Math.trunc(alter) || 0;
    const marks = steps === 0 ? '' : (steps > 0 ? '#' : '-').repeat(Math.abs(steps));
    return `${step}${marks}${octave}`;
}

/** Stabiler Identitätsschlüssel einer Note – Basis für die Mehrfachauswahl (Set-Zugehörigkeit,
 *  Dedup, Toggle). Eine klingende Tonhöhe kann pro (part, measure, voice, voiceNoteIndex, staff)
 *  eindeutig adressiert werden. */
export function noteKey(i: NoteInfo): string {
    const measureIdentity = i.measureIndex === undefined ? `number:${i.measureNumber}` : `index:${i.measureIndex}`;
    return `${i.partId}|${measureIdentity}|${i.voice}|${i.voiceNoteIndex}|${i.staffIndex}`;
}

// ============ Divisions- / Dauer-Mathematik ============

// MusicXML-Notentypen von groß nach klein. Beim „Note teilen" rutscht der Typ eine Stufe nach
// unten (Viertel → Achtel usw.). `<dot>` bleibt erhalten (punktierte kleinere Note).
export const TYPE_LADDER = [
    'breve', 'whole', 'half', 'quarter', 'eighth',
    '16th', '32nd', '64th', '128th', '256th', '512th', '1024th',
];

export function nextSmallerType(type: string): string | null {
    const i = TYPE_LADDER.indexOf(type);
    if (i < 0 || i >= TYPE_LADDER.length - 1) return null; // unbekannt oder bereits kleinster
    return TYPE_LADDER[i + 1] ?? null;
}

// MusicXML-Notentyp → quarterLength (Viertel = 1).
export const TYPE_QUARTER_LENGTH: Record<string, number> = {
    breve: 8, whole: 4, half: 2, quarter: 1, eighth: 0.5,
    '16th': 0.25, '32nd': 0.125, '64th': 0.0625, '128th': 0.03125,
    '256th': 0.015625, '512th': 0.0078125, '1024th': 0.00390625,
};

/** Ticks (in `<divisions>`-Einheiten) für einen Notentyp inkl. Punktierung. */
export function typeToTicks(type: string, dots: number, divisions: number): number {
    const ql = TYPE_QUARTER_LENGTH[type];
    if (!ql || divisions <= 0) return 0;
    const dotFactor = 2 - Math.pow(2, -dots); // 0 Punkte → 1, 1 → 1.5, 2 → 1.75
    return Math.round(ql * dotFactor * divisions);
}

/** Größter Standard-Notentyp, dessen (unpunktierte) Dauer `ticks` nicht überschreitet. */
export function ticksToClosestType(ticks: number, divisions: number): string {
    for (const type of TYPE_LADDER) { // groß → klein
        const t = typeToTicks(type, 0, divisions);
        if (t > 0 && t <= ticks) return type;
    }
    return TYPE_LADDER[TYPE_LADDER.length - 1] ?? 'quarter';
}

/** Exakter Notentyp + Punktierung (0–2 Punkte) für `ticks`, oder `null`, wenn sich die Dauer nicht
 *  als **eine** Note schreiben lässt (z. B. 5 Sechzehntel) – Grundlage für „Noten zusammenführen". */
export function ticksToTypeAndDots(ticks: number, divisions: number): { type: string; dots: number } | null {
    if (ticks <= 0 || divisions <= 0) return null;
    for (const type of TYPE_LADDER) {
        for (let dots = 0; dots <= 2; dots++) {
            if (typeToTicks(type, dots, divisions) === ticks) return {type, dots};
        }
    }
    return null;
}

/** Verdoppelt `<divisions>` UND alle Tick-Werte im Part – `<duration>` (inkl. `<backup>`/`<forward>`)
 *  und das `<offset>` von `<harmony>`/`<direction>`/`<sound>` –, damit eine anschließende Halbierung
 *  ganzzahlig aufgeht und die Zeitrechnung des Parts erhalten bleibt. */
export function doublePartDivisions(part: Element): void {
    for (const div of part.querySelectorAll('divisions')) {
        const v = parseInt(div.textContent || '0', 10);
        if (v > 0) div.textContent = (v * 2).toString();
    }
    for (const ticks of part.querySelectorAll('duration, offset')) {
        const v = parseInt(ticks.textContent || '0', 10);
        if (v) ticks.textContent = (v * 2).toString();   // `<offset>` darf negativ sein
    }
}

/** Setzt `<duration>` und (falls vorhanden) `<type>` einer `<note>` auf die halbierten Werte. */
export function setHalvedDurationAndType(noteEl: Element, halfDuration: number, newType: string | null): void {
    const durEl = noteEl.querySelector('duration');
    if (durEl) durEl.textContent = halfDuration.toString();
    const typeEl = noteEl.querySelector('type');
    if (typeEl && newType) typeEl.textContent = newType;
}

/** `staff` (aus der Referenz-Note geerbt) wird nur angehängt, wenn vorhanden – bei Grand-Staff-Parts
 *  (`<staves>2</staves>`) MUSS jede Note ihren `<staff>` tragen, sonst landet sie im Default-Staff 1.
 *  `<staff>` steht schema-korrekt nach `<type>` (Einzel-Staff-Stücke: `staff=null` → nichts angehängt). */
function buildRestElement(doc: Document, ticks: number, type: string, voice: number, staff: string | null = null): Element {
    const note = doc.createElement('note');
    note.appendChild(doc.createElement('rest'));
    const dur = doc.createElement('duration');
    dur.textContent = ticks.toString();
    note.appendChild(dur);
    const v = doc.createElement('voice');
    v.textContent = voice.toString();
    note.appendChild(v);
    if (type) {
        const t = doc.createElement('type');
        t.textContent = type;
        note.appendChild(t);
    }
    if (staff) {
        const s = doc.createElement('staff');
        s.textContent = staff;
        note.appendChild(s);
    }
    return note;
}

/** Zerlegt `ticks` in eine Folge von Standard-Pausen (greedy groß→klein). Ein nicht exakt
 *  zerlegbarer Rest wird als einzelne Pause mit der exakten Dauer ausgegeben (Timing bleibt
 *  erhalten – der Typ ist dann nur grafisch angenähert). `staff` wird auf jede Pause übernommen
 *  (Grand-Staff-korrekt; `null` bei Einzel-Staff). */
export function ticksToRestElements(doc: Document, ticks: number, divisions: number, voice: number, staff: string | null = null): Element[] {
    const out: Element[] = [];
    let remaining = ticks;
    for (const type of TYPE_LADDER) {
        const t = typeToTicks(type, 0, divisions);
        if (t <= 0) continue;
        while (remaining >= t) {
            out.push(buildRestElement(doc, t, type, voice, staff));
            remaining -= t;
        }
        if (remaining === 0) break;
    }
    if (remaining > 0) {
        out.push(buildRestElement(doc, remaining, ticksToClosestType(remaining, divisions), voice, staff));
    }
    return out;
}

export function buildNoteElement(
    doc: Document, payload: ClipboardNote, ticks: number, type: string, dots: number, voice: number, staff: string | null = null,
): Element {
    const note = doc.createElement('note');
    if (payload.isRest) {
        note.appendChild(doc.createElement('rest'));
    } else {
        const pitch = doc.createElement('pitch');
        const step = doc.createElement('step');
        step.textContent = payload.step;
        pitch.appendChild(step);
        if (payload.alter !== 0) {
            const alter = doc.createElement('alter');
            alter.textContent = payload.alter.toString();
            pitch.appendChild(alter);
        }
        const octave = doc.createElement('octave');
        octave.textContent = payload.octave.toString();
        pitch.appendChild(octave);
        note.appendChild(pitch);
    }
    const dur = doc.createElement('duration');
    dur.textContent = ticks.toString();
    note.appendChild(dur);
    const v = doc.createElement('voice');
    v.textContent = voice.toString();
    note.appendChild(v);
    if (type) {
        const t = doc.createElement('type');
        t.textContent = type;
        note.appendChild(t);
    }
    for (let i = 0; i < dots; i++) note.appendChild(doc.createElement('dot'));
    // `<staff>` (aus der Referenz-Note geerbt) schema-korrekt nach `<dot>`. Grand-Staff-Parts brauchen
    // ihn zwingend (sonst Default-Staff 1); Einzel-Staff-Stücke: `staff=null` → nichts angehängt.
    if (staff) {
        const s = doc.createElement('staff');
        s.textContent = staff;
        note.appendChild(s);
    }
    return note;
}

/** Entfernt eine ganze „Zählzeit": die primäre Note plus alle unmittelbar folgenden
 *  `<note><chord/>`-Begleiter (gleiche Onset-Zeit). */
export function removeBeat(primary: Element): void {
    let sib = primary.nextElementSibling;
    primary.remove();
    while (sib && sib.tagName === 'note' && sib.querySelector('chord')) {
        const next = sib.nextElementSibling;
        sib.remove();
        sib = next;
    }
}

// ============ Lesen ============

export function getNoteFromMusicXml(
    musicXml: string | null,
    measureNumber: number,
    voiceNoteIndex: number,
    voice = 1,
    partId = '',
    staffIndex = 0,
    measureIndex?: number,
): NoteInfo | null {
    try {
        if (!musicXml) return null;
        const doc = parseMusicXml(musicXml);

        // Auf den korrekten <part> einschränken (mehrteilige Partituren haben pro Part eine
        // <measure number="N"> – ohne Part-Scope träfe man immer den ersten Part). Via staffIndex,
        // da OSMDs partId/IdString unzuverlässig ist (leer oder gleich/falsch für mehrere Parts).
        const part = findPartForNote(doc, staffIndex);
        const measure = part
            ? (measureIndex === undefined
                ? findMeasureInPart(part, measureNumber)
                : findMeasureInPartByIndex(part, measureIndex))
            : null;
        if (!measure) return null;
        const resolvedMeasureNumber = parseInt(measure.getAttribute('number') || '0', 10);

        const note = getVoiceNotes(measure, voice)[voiceNoteIndex];
        if (!note) return null;

        const isRest = note.querySelector('rest') !== null;
        const pitchEl = note.querySelector('pitch');
        const duration = note.querySelector('type')?.textContent || '';

        if (isRest) {
            return {
                frequency: 0,
                semitones: 0,
                measureNumber: resolvedMeasureNumber,
                measureIndex,
                noteIndex: voiceNoteIndex,
                voiceNoteIndex,
                staffIndex,
                partId,
                step: '',
                alter: 0,
                octave: 4,
                duration,
                voice,
                isRest: true,
            };
        }

        if (!pitchEl) return null;

        const step = pitchEl.querySelector('step')?.textContent || 'C';
        const alter = parseInt(pitchEl.querySelector('alter')?.textContent || '0', 10);
        const octave = parseInt(pitchEl.querySelector('octave')?.textContent || '4', 10);

        return {
            semitones: 0,
            frequency: 0,
            measureNumber: resolvedMeasureNumber,
            measureIndex,
            noteIndex: voiceNoteIndex,
            voiceNoteIndex,
            staffIndex,
            partId,
            step,
            alter,
            octave,
            duration,
            voice,
            isRest: false,
        };
    } catch (e) {
        console.error('Error getting note from MusicXML:', e);
    }
    return null;
}

// ============ Tonhöhe ↔ Halbton ============

/** Halbton-Namen mit Kreuz-Schreibweise der schwarzen Tasten (Default von `semitoneToPitch`;
 *  identisch zur früheren NoteEditView-Anzeige). */
export const NOTE_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
/** Halbton-Namen mit B-Schreibweise der schwarzen Tasten (für Abwärts-Transposition). */
export const NOTE_NAMES_FLAT = ['C', 'Db', 'D', 'Eb', 'E', 'F', 'Gb', 'G', 'Ab', 'A', 'Bb', 'B'];
/** Schreibweise schwarzer Tasten: Kreuz (C#) oder B (Db). */
export type AccidentalPreference = 'sharp' | 'flat';

const STEP_SEMITONES: Record<string, number> = {C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11};

/** Absoluter Halbton (C0 = 0) aus step/alter/octave. Exakte Inverse zu `semitoneToPitch`, damit
 *  ein Transpose (lesen → +delta → schreiben) verlustfrei round-trippt. */
export function pitchToSemitone(step: string, alter: number, octave: number): number {
    return octave * 12 + (STEP_SEMITONES[step] ?? 0) + alter;
}

/** MIDI-Notennummer aus step/alter/octave (MusicXML-Oktave: C4 = 60 = mittleres C). Die
 *  MusicXML-/music21-Oktave zählt eine tiefer als die MIDI-Oktave, daher `+ 12`. */
export function pitchToMidi(step: string, alter: number, octave: number): number {
    return pitchToSemitone(step, alter, octave) + 12;
}

/** Absoluter Halbton → step/alter/octave. Schwarze Tasten werden je nach `prefer` als Kreuz
 *  (`'sharp'`, Default: C#) oder B (`'flat'`: Db) geschrieben; weiße Tasten sind in beiden Modi
 *  gleich. Inverse zu `pitchToSemitone`; wickelt Oktaven korrekt (B4+1 → C5, C4−1 → B3). */
export function semitoneToPitch(
    semitone: number, prefer: AccidentalPreference = 'sharp',
): { step: string; alter: number; octave: number } {
    const octave = Math.floor(semitone / 12);
    const noteIndex = ((semitone % 12) + 12) % 12; // negatives Modulo abfangen
    const name = (prefer === 'flat' ? NOTE_NAMES_FLAT : NOTE_NAMES)[noteIndex] ?? '';
    if (name.includes('#')) return {step: name[0] ?? '', alter: 1, octave};
    if (name.includes('b')) return {step: name[0] ?? '', alter: -1, octave};
    return {step: name, alter: 0, octave};
}

/** Schreibt step/alter/octave in die `<pitch>` einer `<note>` (kein Effekt bei Pausen ohne
 *  `<pitch>`). Ändert sich der Ton (step/alter), wird ein vorhandenes Anzeige-`<accidental>`
 *  entfernt – es beschreibt die alte Tonhöhe und OSMD würde sonst z. B. ein Kreuz an einem Db
 *  zeigen; OSMD/music21 leiten das Vorzeichen aus `<alter>` + Tonart selbst ab. Eine reine
 *  Oktavänderung lässt es stehen. Genutzt von `transposeNotesInMusicXml`. */
export function setPitch(doc: Document, noteEl: Element, step: string, alter: number, octave: number): void {
    const pitchEl = noteEl.querySelector('pitch');
    if (!pitchEl) return;
    const stepEl = pitchEl.querySelector('step');
    const prevStep = stepEl?.textContent || '';
    const prevAlter = parseInt(pitchEl.querySelector('alter')?.textContent || '0', 10) || 0;
    if (prevStep !== step || prevAlter !== alter) noteEl.querySelector(':scope > accidental')?.remove();
    if (stepEl) stepEl.textContent = step;

    let alterEl = pitchEl.querySelector('alter');
    if (alter !== 0) {
        if (!alterEl) {
            alterEl = doc.createElement('alter');
            const stepElement = pitchEl.querySelector('step');
            if (stepElement?.nextSibling) pitchEl.insertBefore(alterEl, stepElement.nextSibling);
            else pitchEl.appendChild(alterEl);
        }
        alterEl.textContent = alter.toString();
    } else if (alterEl) {
        alterEl.remove();
    }

    const octaveEl = pitchEl.querySelector('octave');
    if (octaveEl) octaveEl.textContent = octave.toString();
}


// ============ Geteilte Basis-Helfer (aus den Domänen-Modulen hierher gehoben, damit
// Geschwister-Module keine Zyklen bilden) ============

export interface Pitch {
    step: string;
    alter: number;
    octave: number;
}

export function pitchOfNote(noteEl: Element): Pitch | null {
    const pitchEl = noteEl.querySelector('pitch');
    if (!pitchEl) return null;
    return {
        step: pitchEl.querySelector('step')?.textContent || '',
        alter: parseInt(pitchEl.querySelector('alter')?.textContent || '0', 10) || 0,
        octave: parseInt(pitchEl.querySelector('octave')?.textContent || '4', 10),
    };
}

export function samePitch(a: Pitch, b: Pitch): boolean {
    return a.step === b.step && a.alter === b.alter && a.octave === b.octave;
}

/** Alle `<note>` einer Akkord-Gruppe (führende `<note>` ohne `<chord/>` + folgende `<note><chord/>`-
 *  Begleiter) zu einer geklickten Note. Einzelnote → `[noteEl]`. Erkennung wie `removeBeat`. */
export function chordGroupOf(noteEl: Element): Element[] {
    const isCompanion = (n: Element) => !!n.querySelector('chord');
    let primary = noteEl;
    while (isCompanion(primary)) {
        const prev = primary.previousElementSibling;
        if (prev && prev.tagName === 'note') primary = prev;
        else break;
    }
    const group = [primary];
    let sib = primary.nextElementSibling;
    while (sib && sib.tagName === 'note' && isCompanion(sib)) {
        group.push(sib);
        sib = sib.nextElementSibling;
    }
    return group;
}

/**
 * Zwischenablage-Nutzlast einer kopierten Note (Strg+C). Rhythmus wird divisions-unabhängig als
 * `type` + `dots` gespeichert; `durationTicks`/`sourceDivisions` erlauben die exakte Skalierung
 * auf die `<divisions>` des Zieltakts beim Einfügen (Strg+Umschalt+V).
 */
export interface ClipboardNote {
    isRest: boolean;
    step: string;           // C..B (leer bei Pause)
    alter: number;          // -1 flat, 0 natural, 1 sharp
    octave: number;
    type: string;           // MusicXML-Notentyp (quarter, eighth, …)
    dots: number;           // Anzahl <dot>
    durationTicks: number;  // <duration> im Quell-Part
    sourceDivisions: number;// <divisions> des Quell-Parts (für Skalierung beim Einfügen)
}

/** Verschiebt `<beam>` und `<lyric>` (Primärnoten-Attribute einer Zählzeit) von `from` nach `to`
 *  – schema-korrekt (`<beam>` vor `<notations>`/`<lyric>`, `<lyric>` ans Ende). */
export function movePrimaryChildren(from: Element, to: Element): void {
    const beams = [...from.querySelectorAll(':scope > beam')];
    const anchor = to.querySelector(':scope > notations') ?? to.querySelector(':scope > lyric');
    for (const b of beams) {
        if (anchor) to.insertBefore(b, anchor);
        else to.appendChild(b);
    }
    for (const l of [...from.querySelectorAll(':scope > lyric')]) to.appendChild(l);
}
